import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Done as DoneIcon,
  Payment as PaymentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { formatVendorCurrency } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';
import CustomInvoiceItemForm from '../components/CustomInvoiceItemForm';

interface VendorInvoice {
  id: number;
  vendor_firm_id: number;
  vendor_user_id: number;
  invoice_number: string;
  invoice_date: string;
  period_start: string;
  period_end: string;
  opening_balance: number;
  total_billing: number;
  total_payments: number;
  net_amount: number;
  currency: string;
  billing_currency?: string | null;
  conversion_rate?: number | null;
  original_amount?: number | null;
  converted_amount?: number | null;
  original_total_billing?: number | null;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'final' | 'sent' | 'paid' | 'overdue';
  notes: string;
  html_content: string | null;
  pdf_path: string | null;
  pdf_generated_at: string | null;
  created_at: string;
  updated_at: string;
  total_paid: number;
  remaining_balance: number;
  payment_status: 'unpaid' | 'partial' | 'paid';
  payments: VendorInvoicePayment[];
  vendor_firm: {
    id: number;
    name: string;
    address_1: string;
    address_2: string | null;
    city: string;
    state: string;
    zip_code: string;
    country: string;
  };
  vendor_user: {
    id: number;
    name: string;
    email: string;
    currency: string;
    billing_currency?: string | null;
    address_1: string;
    address_2: string | null;
    city: string;
    state: string;
    zip_code: string;
    country: string;
  };
  created_by: {
    id: number;
    name: string;
  };
  items: VendorInvoiceItem[];
}

interface VendorInvoiceItem {
  id: number;
  vendor_invoice_id: number;
  case_estimate_id: number | null;
  case_id: number | null;
  case_title: string | null;
  billing_type: string;
  rate: number;
  quantity: number;
  amount: number;
  description: string | null;
  is_custom: boolean;
  custom_title: string | null;
  created_at: string;
  updated_at: string;
  case: any | null;
  case_estimate: any | null;
}

interface VendorInvoicePayment {
  id: number;
  vendor_invoice_id: number;
  amount: number;
  payment_method: string;
  reference_number: string | null;
  payment_date: string;
  notes: string | null;
  recorded_by: {
    id: number;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

const VendorInvoiceView: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<VendorInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  
  // Conversion rate dialog state
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
  const [conversionRate, setConversionRate] = useState('');
  const [updatingConversion, setUpdatingConversion] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    reference_number: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
    tds_rate: 10.00
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  const canRecalculateTotals = !!invoice;

  useEffect(() => {
    if (invoiceId) {
      loadInvoice();
    }
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/vendor-invoices/${invoiceId}`);
      if (response.data.success) {
        console.log('Invoice data loaded:', response.data.data);
        console.log('Vendor Firm:', response.data.data.vendor_firm);
        console.log('Vendor User:', response.data.data.vendor_user);
        setInvoice(response.data.data);
      }
    } catch (err: any) {
      console.error('Error loading invoice:', err);
      setError(err.response?.data?.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveInvoice = async () => {
    if (!invoice) return;
    
    try {
      const response = await api.post(`/vendor-invoices/${invoice.id}/approve-by-admin`);
      if (response.data.success) {
        setSuccess('Invoice finalized successfully');
        loadInvoice(); // Reload to get updated status
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to finalize invoice');
    }
  };

  const handleRejectInvoice = async () => {
    if (!invoice) return;
    
    const rejectionReason = prompt('Please provide a reason for rejection:');
    if (!rejectionReason) return;

    try {
      const response = await api.post(`/vendor-invoices/${invoice.id}/reject-by-admin`, {
        rejection_reason: rejectionReason
      });
      if (response.data.success) {
        setSuccess('Invoice rejected successfully');
        loadInvoice(); // Reload to get updated status
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject invoice');
    }
  };

  const handleFinalizeInvoice = async () => {
    if (!invoice) return;
    
    try {
      const response = await api.post(`/vendor-invoices/${invoice.id}/finalize`);
      if (response.data.success) {
        setSuccess('Invoice finalized successfully');
        loadInvoice(); // Reload to get updated status
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to finalize invoice');
    }
  };

  const handleUpdateConversionRate = async () => {
    if (!invoice || !conversionRate) return;
    
    try {
      setUpdatingConversion(true);
      const response = await api.put(`/vendor-invoices/${invoice.id}/conversion-rate`, {
        conversion_rate: parseFloat(conversionRate)
      });
      if (response.data.success) {
        setSuccess('Conversion rate updated successfully');
        setConversionDialogOpen(false);
        setConversionRate('');
        loadInvoice(); // Reload to get updated amounts
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update conversion rate');
    } finally {
      setUpdatingConversion(false);
    }
  };

  const openConversionDialog = () => {
    if (invoice) {
      setConversionRate(invoice.conversion_rate?.toString() || '');
      setConversionDialogOpen(true);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'sent': return 'warning';
      case 'draft': return 'default';
      case 'pending_approval': return 'warning';
      case 'approved': return 'info';
      case 'rejected': return 'error';
      case 'final': return 'success';
      default: return 'default';
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    
    try {
      const response = await api.get(`/vendor-invoices/${invoice.id}/download`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download PDF');
    }
  };

  const handleRecalculateTotals = async () => {
    if (!invoice) return;

    try {
      const response = await api.post(`/vendor-invoices/${invoice.id}/recalculate-totals`);
      if (response.data.success) {
        setSuccess('Invoice totals recalculated successfully');
        setInvoice(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to recalculate invoice totals');
    }
  };

  const handleRecordPayment = async () => {
    if (!invoice) return;
    
    try {
      setPaymentLoading(true);
      const response = await api.post(`/vendor-invoices/${invoice.id}/payments`, paymentForm);
      if (response.data.success) {
        setSuccess('Payment recorded successfully');
        setPaymentDialogOpen(false);
        setPaymentForm({
          amount: '',
          payment_method: 'bank_transfer',
          reference_number: '',
          payment_date: new Date().toISOString().split('T')[0],
          notes: '',
          tds_rate: 10.00
        });
        loadInvoice(); // Reload to get updated payment status
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    if (!invoice) return;
    
    if (!window.confirm('Are you sure you want to delete this payment record?')) return;
    
    try {
      const response = await api.delete(`/vendor-invoices/${invoice.id}/payments/${paymentId}`);
      if (response.data.success) {
        setSuccess('Payment deleted successfully');
        loadInvoice(); // Reload to get updated payment status
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete payment');
    }
  };

  const openPaymentDialog = () => {
    if (invoice) {
      setPaymentForm(prev => ({
        ...prev,
        amount: invoice.remaining_balance.toString()
      }));
    }
    setPaymentDialogOpen(true);
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'partial': return 'warning';
      case 'unpaid': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number, useBillingCurrency: boolean = false) => {
    // Use billing currency if conversion is applied, otherwise use regular currency
    const currency = useBillingCurrency && invoice?.billing_currency 
      ? invoice.billing_currency 
      : invoice?.vendor_user?.currency || invoice?.currency;
    return formatVendorCurrency(amount, currency);
  };

  // Format billing type code to user-friendly label
  const getBillingTypeLabel = (type: string) => {
    switch (type) {
      case 'page_based': return 'Page Based';
      case 'case_based': return 'Case Based';
      case 'time_based': return 'Time Based';
      case 'flat_rate': return 'Flat Rate';
      default: return type ? type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box>
        <Alert severity="error">Invoice not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/vendor-invoices')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4">Invoice Details</Typography>
          <Typography variant="body2" color="text.secondary">
            {invoice.invoice_number}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Invoice Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 3, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Billing
            </Typography>
            <Typography variant="h5">
              {formatCurrency(invoice.total_billing || 0, true)}
            </Typography>
            {invoice.original_total_billing && invoice.billing_currency && invoice.currency !== invoice.billing_currency && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                Original: {formatVendorCurrency(invoice.original_total_billing, invoice.currency)}
              </Typography>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Net Amount
            </Typography>
            <Typography variant="h5" color="primary">
              {formatCurrency(invoice.net_amount || 0, true)}
            </Typography>
            {invoice.billing_currency && invoice.currency !== invoice.billing_currency && (
              <>
                {invoice.original_amount && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Original: {formatVendorCurrency(invoice.original_amount, invoice.currency)}
                  </Typography>
                )}
                    {invoice.converted_amount && invoice.conversion_rate && (
                      <Typography variant="body2" color="text.secondary">
                        Converted: {formatVendorCurrency(invoice.converted_amount, invoice.billing_currency)}
                        {invoice.conversion_rate && (
                          <span> (Rate: {Number(invoice.conversion_rate).toFixed(4)})</span>
                        )}
                      </Typography>
                    )}
                {!invoice.conversion_rate && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={openConversionDialog}
                    sx={{ mt: 1 }}
                  >
                    Add Conversion Rate
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Paid
            </Typography>
            <Typography variant="h5" color="success.main">
              {formatCurrency(invoice.total_paid || 0)}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Remaining Balance
            </Typography>
            <Typography variant="h5" color={invoice.remaining_balance > 0 ? "warning.main" : "success.main"}>
              {formatCurrency(invoice.remaining_balance || 0)}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Payment Status
            </Typography>
            <Chip
              label={invoice.payment_status?.toUpperCase() || 'UNPAID'}
              color={getPaymentStatusColor(invoice.payment_status || 'unpaid') as any}
              size="small"
            />
            <Box sx={{ mt: 1 }}>
              <Chip
                label={invoice.status === 'pending_approval' ? 'AWAITING PMP APPROVAL' : 
                       invoice.status === 'final' ? 'FINALIZED' :
                       invoice.status.toUpperCase()}
                color={getStatusColor(invoice.status) as any}
                size="small"
                variant="outlined"
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        {/* Invoice Details */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Invoice Information
          </Typography>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Invoice Number</Typography>
              <Typography variant="body1">{invoice.invoice_number}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Invoice Date</Typography>
              <Typography variant="body1">
                {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Period</Typography>
              <Typography variant="body1">
                {invoice.period_start ? new Date(invoice.period_start).toLocaleDateString() : 'N/A'} - {invoice.period_end ? new Date(invoice.period_end).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Opening Balance</Typography>
              <Typography variant="body1">{formatCurrency(invoice.opening_balance || 0)}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Total Payments</Typography>
              <Typography variant="body1">{formatCurrency(invoice.total_payments || 0)}</Typography>
            </Box>
            {invoice.notes && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                <Typography variant="body1">{invoice.notes}</Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Vendor Information */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Vendor Information
          </Typography>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Vendor Firm</Typography>
              <Typography variant="body1">
                {invoice.vendor_firm?.name || 
                 (invoice.vendor_user?.name ? `${invoice.vendor_user.name} (Individual Vendor)` : 'No vendor information available')}
              </Typography>
              {(invoice.vendor_firm?.address_1 || invoice.vendor_user?.address_1) && (
                <Typography variant="body2" color="text.secondary">
                  {invoice.vendor_firm?.address_1 || invoice.vendor_user?.address_1}
                  {(invoice.vendor_firm?.address_2 || invoice.vendor_user?.address_2) && 
                   `, ${invoice.vendor_firm?.address_2 || invoice.vendor_user?.address_2}`}
                  {(invoice.vendor_firm?.city || invoice.vendor_user?.city) && 
                   `, ${invoice.vendor_firm?.city || invoice.vendor_user?.city}`}
                  {(invoice.vendor_firm?.state || invoice.vendor_user?.state) && 
                   `, ${invoice.vendor_firm?.state || invoice.vendor_user?.state}`}
                  {(invoice.vendor_firm?.zip_code || invoice.vendor_user?.zip_code) && 
                   ` ${invoice.vendor_firm?.zip_code || invoice.vendor_user?.zip_code}`}
                  {(invoice.vendor_firm?.country || invoice.vendor_user?.country) && 
                   `, ${invoice.vendor_firm?.country || invoice.vendor_user?.country}`}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Vendor User</Typography>
              <Typography variant="body1">
                {invoice.vendor_user?.name || 'No vendor user information available'}
              </Typography>
              {invoice.vendor_user?.email && (
                <Typography variant="body2" color="text.secondary">
                  {invoice.vendor_user.email}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
              <Typography variant="body1">
                {invoice.created_by?.name || 'System Generated'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Invoice Items */}
      <Paper sx={{ mt: 3 }}>
        <Box sx={{ p: 3, pb: 1 }}>
          <Typography variant="h6" gutterBottom>
            Invoice Items
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Case</TableCell>
                <TableCell>Billing Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Amount</TableCell>
                {invoice.billing_currency && invoice.currency !== invoice.billing_currency && invoice.conversion_rate && (
                  <TableCell align="right">Converted Amount ({invoice.billing_currency})</TableCell>
                )}
              </TableRow>
            </TableHead>
                            <TableBody>
              {invoice.items?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.is_custom ? item.custom_title : (item.case_title || 'N/A')}
                  </TableCell>
                  <TableCell>{getBillingTypeLabel(item.billing_type || '')}</TableCell>
                  <TableCell>{item.description || item.case_estimate?.description || 'N/A'}</TableCell>
                  <TableCell align="right">{item.quantity || 0}</TableCell>
                  <TableCell align="right">{formatCurrency(item.rate || 0)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.amount || 0)}</TableCell>
                  {invoice.billing_currency && invoice.currency !== invoice.billing_currency && invoice.conversion_rate && (
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium" color="primary">
                        {formatVendorCurrency(
                          (item.amount || 0) * Number(invoice.conversion_rate),
                          invoice.billing_currency
                        )}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Custom Invoice Items Form */}
      <CustomInvoiceItemForm
        invoiceId={invoice.id}
        invoiceStatus={invoice.status}
        onItemAdded={loadInvoice}
        onItemUpdated={loadInvoice}
        isPmpAdmin={user?.isPmpAdmin?.() || false}
        isInvoiceCreator={user?.id === invoice.created_by?.id}
      />

      {/* Payments Section */}
      {(invoice.payments && invoice.payments.length > 0) || invoice.remaining_balance > 0 ? (
        <Paper sx={{ mt: 3 }}>
          <Box sx={{ p: 3, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Payment Records
            </Typography>
            {invoice.remaining_balance > 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openPaymentDialog}
                color="success"
              >
                Record Payment
              </Button>
            )}
          </Box>
          {invoice.payments && invoice.payments.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Recorded By</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {new Date(payment.payment_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          {formatCurrency(payment.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={payment.payment_method.replace('_', ' ').toUpperCase()} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{payment.reference_number || '-'}</TableCell>
                      <TableCell>{payment.recorded_by?.name || '-'}</TableCell>
                      <TableCell>{payment.notes || '-'}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeletePayment(payment.id)}
                          title="Delete Payment"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No payments recorded yet</Typography>
            </Box>
          )}
        </Paper>
      ) : null}

      {/* Actions */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {canRecalculateTotals && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleRecalculateTotals}
          >
            Recalculate Net Amount
          </Button>
        )}
        {invoice.status === 'pending_approval' && (
          <>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleApproveInvoice}
            >
              Finalize Invoice
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleRejectInvoice}
            >
              Reject Invoice
            </Button>
          </>
        )}
        {invoice.status === 'approved' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<DoneIcon />}
            onClick={handleFinalizeInvoice}
          >
            Finalize Invoice
          </Button>
        )}
        {invoice.status === 'final' && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPdf}
          >
            Download PDF
          </Button>
        )}
      </Box>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* Conversion Rate Dialog */}
      {invoice && invoice.billing_currency && invoice.currency !== invoice.billing_currency && (
        <Dialog open={conversionDialogOpen} onClose={() => setConversionDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Conversion Rate</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Currency: {invoice.currency} → Billing Currency: {invoice.billing_currency}
              </Alert>
              <TextField
                fullWidth
                label={`Conversion Rate (${invoice.currency} to ${invoice.billing_currency})`}
                type="number"
                value={conversionRate}
                onChange={(e) => setConversionRate(e.target.value)}
                inputProps={{ min: 0.0001, step: 0.0001 }}
                helperText={`Enter the conversion rate from ${invoice.currency} to ${invoice.billing_currency}`}
                sx={{ mb: 2 }}
              />
              {invoice.original_amount && conversionRate && parseFloat(conversionRate) > 0 && (
                <Alert severity="success">
                  Converted Amount: {formatVendorCurrency(
                    invoice.original_amount * parseFloat(conversionRate),
                    invoice.billing_currency
                  )}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConversionDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleUpdateConversionRate}
              variant="contained"
              disabled={!conversionRate || parseFloat(conversionRate) <= 0 || updatingConversion}
            >
              {updatingConversion ? <CircularProgress size={20} /> : 'Update Conversion Rate'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon color="primary" />
            Record Payment
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Payment Amount"
              type="number"
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
              inputProps={{ min: 0.01, max: invoice?.remaining_balance || 0, step: 0.01 }}
              helperText={`Maximum: ${formatCurrency(invoice?.remaining_balance || 0)}`}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="TDS Rate (%)"
              type="number"
              value={paymentForm.tds_rate}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, tds_rate: parseFloat(e.target.value) || 0 }))}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              helperText={`TDS Amount: ${formatCurrency((parseFloat(paymentForm.amount) || 0) * (paymentForm.tds_rate / 100))}`}
              sx={{ mb: 2 }}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentForm.payment_method}
                label="Payment Method"
                onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
              >
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="check">Check</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="online_transfer">Online Transfer</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Reference Number"
              value={paymentForm.reference_number}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, reference_number: e.target.value }))}
              helperText="Transaction ID, Check number, etc."
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Payment Date"
              type="date"
              value={paymentForm.payment_date}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
              helperText="Optional notes about the payment"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRecordPayment}
            variant="contained"
            disabled={!paymentForm.amount || paymentLoading}
            startIcon={paymentLoading ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {paymentLoading ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorInvoiceView;
